/**
 * Следит за попаданием во вьюпорт всех добавленных элементов
 * @class Observer
 */
export class Observer {
  /**
   * Конструктор
   * @param {ConstructorParams} params
   * @param {Element=} params.root Элемент, пересечение с которым нужно наблюдать.
   * По-умолчанию это вьюпорт.
   * @param {string?} params.rootMargin Отступы от элемента, включаемые в расчет пересечения.
   * Пишутся в стиле `margin` в CSS. `1px 2px 3px 4px`
   * @param {number=} params.threshold Какая часть элемента должна пересекаться с рутом
   * для возникновения события пересечения. Значение от 0 до 1.
   * @param {class IntersectionObserver=} params.Observer
   */
  constructor({
    root = null,
    rootMargin = '0px 0px 0px 0px',
    threshold = 0,
    Observer = IntersectionObserver,
  }) {
    this.entries = new Map();
    this.destroyed = false;
    this.currentRoot = null;


    this.currentRoot = root;
    this.currentRootMargin = rootMargin;
    this.currentThreshold = threshold;
    this.Observer = Observer;
    this.observer = this.recreateObserver();
  }

  /**
   * Элемент, за пересечением с которым будет следить IntersectionObserver.
   * При значении `null` - элементом считается вьюпорт
   */
  get root() {
    this.checkStatus('root', 'getter');
    return this.currentRoot;
  }
  set root(val) {
    this.checkStatus('root', 'setter');
    if (val === this.currentRoot) {
      return;
    }
    this.currentRoot = val;
    this.observer = this.recreateObserver();
  }

  /**
   * Отступы элемента root. Расширяют границы, при достижении которых определяется пересечение
   * @returns string
   */
  get rootMargin() {
    this.checkStatus('rootMargin', 'getter');
    return this.currentRootMargin;
  }
  /**
   * @param string
   */
  set rootMargin(val) {
    this.checkStatus('rootMargin', 'setter');
    if (val === this.currentRootMargin) {
      return;
    }
    this.currentRootMargin = val;
    this.observer = this.recreateObserver();
  }

  /**
   * Определяет, какая чать observable элемента должна пересекать root,
   * чтобы пересечение считалось активным.
   * Принимает значение от 0 до 1.
   */
  get threshold() {
    this.checkStatus('threshold', 'getter');
    return this.currentThreshold;
  }
  set threshold(val) {
    this.checkStatus('threshold', 'setter');
    if (val === this.currentThreshold) {
      return;
    }
    this.currentThreshold = val;
    this.observer = this.recreateObserver();
  }

  /**
   * Добавляет элемент под наблюдение за пересечением с вьюпортом (или заданным root).
   * Вызывает переданные коллбэки.
   * @param {Element} entry Элемент, который нужно наблюдать.
   * @param {IntersectionCallbacks} callbacks
   * @param {Function} callbacks.onIntersection Функция, вызываемая на пересечение.
   * @param {Function} callbacks.onOutIntersection Функция, вызываемая на выход из пересечения.
   */
  add(entry, callbacks) {
    this.checkStatus('add');

    if (this.entries) {
      this.entries.set(entry, { ...callbacks, wasIntersecting: null });

    }

    this.observer.observe(entry);
  }

  /**
   * Убирает элемент из-под наблюдения
   * @param {Element} entry Элемент, который нужно убрать из-под наблюдения.
   */
  remove(entry) {
    this.checkStatus('remove');
    const hasEntry = this.entries && this.entries.has(entry) ? true : false;
    if (hasEntry) {
      this.entries.delete(entry);
      this.observer.unobserve(entry);
    }
  }

  /**
   * Уничтожает объект класса.
   * Отписывается ото всех эвентов, удаляет ненужные свойства.
   */
  destroy() {
    this.checkStatus('destroy');
    delete this.entries;
    this.observer.disconnect();
    this.destroyed = true;
  }

  recreateObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    const observer = new this.Observer(this.observerCallback.bind(this), {
      threshold: this.threshold,
      root: this.root,
      rootMargin: this.rootMargin,
    });

    if (this.entries) {
      this.entries.forEach((_, element) => {
        this.observer.observe(element);
      });
    }

    return observer;
  }

  observerCallback(entries) {
    entries.forEach((entry) => {
      const target = this.entries && this.entries.get(entry.target);
      if (entry.isIntersecting === target.wasIntersecting) {
        return;
      }

      if (entry.isIntersecting && 'onIntersection' in target) {
        target.onIntersection(entry);
      } else if (!entry.isIntersecting && 'onOutIntersection' in target) {
        target.onOutIntersection(entry);
      }

      if (this.entries) {
        this.entries.set(entry.target, { ...target, wasIntersecting: entry.isIntersecting });
      }
    });
  }

  checkStatus(methodName, type = 'method') {
    if (this.destroyed) {
      let message = 'Observer был уничтожен до вызова ';
      // eslint-disable-next-line default-case
      switch (type) {
        case 'method':
          message += 'метода';
          break;
        case 'setter':
          message += 'сеттера';
          break;
        case 'getter':
          message += 'геттера';
          break;
      }

      throw new Error(`${message} "${methodName}"`);
    }
  }
}

export default Observer;



// WEBPACK FOOTER //
// ./src/shared/intersect/observer.js